import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, Plus } from "lucide-react";
import { methodLabel } from "@/api/payments";
import { fetchReceiptPdf, receiptFileName, useReceipts, type ReceiptRow } from "@/api/receipts";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { Button, buttonClass } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { saveBlob } from "@/lib/files";
import { formatDate, formatMoney } from "@/lib/format";
import { totalsByCurrency } from "@/lib/money";

/** What was handed over, when that is not the currency invoiced. */
function receivedAs(receipt: ReceiptRow): string {
  const how = methodLabel(receipt.method);
  if (!receipt.received_currency || !receipt.received_amount) return how;
  return `${how} · ${formatMoney(receipt.received_amount, receipt.received_currency)}`;
}

function DownloadButton({ receipt }: { receipt: ReceiptRow }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const version = receipt.document_version;

  function download(): void {
    if (version === null) return;
    setBusy(true);
    setError(null);
    fetchReceiptPdf(receipt.id, version, receiptFileName(receipt))
      .then((file) => saveBlob(file.blob, file.filename))
      .catch((err: unknown) => setError(err))
      .finally(() => setBusy(false));
  }

  if (version === null) return <span className="text-xs text-muted-foreground">No PDF</span>;
  return (
    <>
      <Button size="sm" variant="outline" onClick={download} disabled={busy}>
        <Download className="h-3.5 w-3.5" aria-hidden />
        {busy ? "Preparing…" : "PDF"}
      </Button>
      {error !== null && <RequestError error={error} />}
    </>
  );
}

export function ReceiptsPage() {
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const receipts = useReceipts({ includeCancelled });

  const live = (receipts.data ?? []).filter((receipt) => receipt.state !== "cancelled");
  const acknowledged = totalsByCurrency(
    live,
    (receipt) => receipt.currency,
    (receipt) => receipt.amount,
  );
  const cashCount = live.filter((receipt) => receipt.method === "cash").length;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Receipts"
        description="What we have acknowledged to clients, in its own numbered series."
        actions={
          <Link to="/receipts/new" className={buttonClass("primary", "md")}>
            <Plus className="h-4 w-4" aria-hidden />
            New receipt
          </Link>
        }
      />
      <Checkbox
        id="receipts-cancelled"
        label="Show cancelled receipts"
        checked={includeCancelled}
        onChange={setIncludeCancelled}
      />

      {receipts.isPending ? (
        <p className="text-muted-foreground">Loading receipts…</p>
      ) : receipts.isError ? (
        <RequestError error={receipts.error} />
      ) : receipts.data.length === 0 ? (
        <EmptyState
          title="No receipts yet"
          description="Use New receipt for one receipt covering several invoices, payments, vessels or voyages, or issue one from the payment on its invoice page."
        />
      ) : (
        <>
          <p className="text-sm">
            Acknowledged{" "}
            <strong>
              {acknowledged.map((total) => formatMoney(total.amount, total.currency)).join(" + ")}
            </strong>
            {cashCount > 0 && (
              <span className="text-muted-foreground">
                {" "}
                · {cashCount} received in cash
              </span>
            )}
          </p>

          <ul className="flex flex-col gap-3 md:hidden">
            {receipts.data.map((receipt) => (
              <li
                key={receipt.id}
                className="flex flex-col gap-1 rounded-lg border bg-card p-4 shadow-sm"
              >
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{receipt.receipt_no}</span>
                  {receipt.state === "cancelled" && <Badge variant="destructive">Cancelled</Badge>}
                </span>
                <span className="text-sm">
                  {formatMoney(receipt.amount, receipt.currency)} · {receipt.client_code}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatDate(receipt.receipt_date)} · {receivedAs(receipt)}
                </span>
                <span className="text-xs text-primary">{receipt.invoice_nos.join(", ")}</span>
                <span className="mt-1">
                  <DownloadButton receipt={receipt} />
                </span>
              </li>
            ))}
          </ul>

          <div className="hidden table-frame md:block">
            <table className="data-table">
              <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-medium">Receipt</th>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">Client</th>
                  <th className="px-4 py-2 text-right font-medium">Amount</th>
                  <th className="px-4 py-2 font-medium">Received as</th>
                  <th className="px-4 py-2 font-medium">Settles</th>
                  <th className="px-4 py-2 font-medium">PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {receipts.data.map((receipt) => (
                  <tr key={receipt.id} className={receipt.state === "cancelled" ? "opacity-70" : ""}>
                    <td className="px-4 py-2 font-medium">
                      {receipt.receipt_no}
                      {receipt.state === "cancelled" && (
                        <Badge variant="destructive" className="ml-2">
                          Cancelled
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2">{formatDate(receipt.receipt_date)}</td>
                    <td className="px-4 py-2">{receipt.client_code}</td>
                    <td className="px-4 py-2 text-right font-medium">
                      {formatMoney(receipt.amount, receipt.currency)}
                    </td>
                    <td className="px-4 py-2">{receivedAs(receipt)}</td>
                    <td className="px-4 py-2 text-primary">{receipt.invoice_nos.join(", ")}</td>
                    <td className="px-4 py-2">
                      <DownloadButton receipt={receipt} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            A receipt cannot be edited once issued. To correct one, open the invoice it settles,
            cancel the receipt and issue another. Cancelled numbers are never reused.{" "}
            <Link to="/payments" className="text-primary underline">
              See the payments behind them
            </Link>
            .
          </p>
        </>
      )}
    </div>
  );
}
